import { defaults, each, filter, get, includes, intersection } from 'lodash'

import {
  AddressQuestion,
  AutocompleteQuestion,
  BodySelectorQuestion,
  BooleanQuestion,
  CheckboxQuestion,
  DateQuestion,
  DropdownQuestion,
  FileQuestion,
  ImageQuestion,
  LabelOnlyQuestion,
  MeasurementQuestion,
  MultiDropdownQuestion,
  PhoneQuestion,
  Question,
  QuestionBase,
  Questions,
  RadioQuestion,
  ScoreResultQuestion,
  SliderQuestion,
  TableQuestion,
  TextQuestion,
} from '../../classes/question'
import { IQuestion, QuestionType } from '../../model/question'
import { checkAgeRestriction } from '../check-age-restriction'

interface IRequirements {
  consents?: { [key: string]: boolean }
  sex?: string
  age?: Date
}

export function getCustomQuestions(
  sectionId: string,
  requirements: IRequirements,
  customQuestions: IQuestion[],
): Questions {
  if (!sectionId) {
    return []
  }

  const consentsSelected: string[] = []
  each(requirements.consents, (consented, consentId) => {
    if (consented) {
      consentsSelected.push(consentId)
    }
  })

  if (customQuestions) {
    const questionsFiltered = filter(customQuestions, (question) => {
      const questionRequirements = question.requirements
      if (!questionRequirements) {
        return true
      }

      // determine if this question has consent requirement
      if (
        get(questionRequirements, 'consents') &&
        questionRequirements.consents.length !== 0 &&
        intersection(questionRequirements.consents, consentsSelected).length ===
          0
      ) {
        return false
      }

      // determine if this question has sex requirement
      if (
        get(questionRequirements, 'sexes') &&
        questionRequirements.sexes.length !== 0 &&
        !includes(questionRequirements.sexes, requirements.sex)
      ) {
        return false
      }

      // determine if this question has age requirement
      if (
        questionRequirements &&
        questionRequirements.age &&
        questionRequirements.age.comparison
      ) {
        return checkAgeRestriction(requirements.age, questionRequirements.age)
      }

      return true
    })

    const questions = getNestedQuestions(questionsFiltered)
    return questions
  }
  return []
}

/**
 * Create a nested question's object
 * @param stepQuestions questions array
 */
function getNestedQuestions(stepQuestions: IQuestion[]): Questions {
  return stepQuestions.map((options) => {
    if (options.questions) {
      // force in the ifQuestions to pass them to BooleanQuestion
      ;(options as any).ifQuestions = getNestedQuestions(options.questions)
    }
    return createQuestion(options)
  })
}

function createQuestion(questionData: IQuestion): QuestionBase<any> {
  const ui = get(questionData, 'options.ui', '')

  defaults(questionData, { flex: '100' })

  switch (questionData.type) {
    case QuestionType.Boolean:
      return new BooleanQuestion(questionData)
    case QuestionType.Checkbox:
      return new CheckboxQuestion(questionData)
    case QuestionType.Radio:
      return new RadioQuestion(questionData)
    case QuestionType.Date:
      return new DateQuestion(questionData)
    case QuestionType.Address:
      return new AddressQuestion(questionData)
    case QuestionType.Measurement:
      return new MeasurementQuestion(questionData)
    case QuestionType.Phone:
      return new PhoneQuestion(questionData)
    case QuestionType.File:
      return new FileQuestion(questionData)
    case QuestionType.Image:
      return new ImageQuestion(questionData)
    case QuestionType.BodySelector:
      return new BodySelectorQuestion(questionData)
    case QuestionType.Label:
      return new LabelOnlyQuestion(questionData)
    case QuestionType.ScoreResult:
      return new ScoreResultQuestion(questionData)
    case QuestionType.Table:
      return new TableQuestion(questionData)
    case QuestionType.Slider:
      return new SliderQuestion(questionData)
    case QuestionType.Dropdown:
      switch (ui) {
        case 'autocomplete':
          return new AutocompleteQuestion(questionData)
        case 'multiple':
          return new MultiDropdownQuestion(questionData)
        default:
          // default single
          return new DropdownQuestion(questionData)
      }
    default:
      return ui === 'textarea'
        ? new TextQuestion(questionData)
        : new Question(questionData)
  }
}
